'use client';

import { useTranslations } from 'next-intl';
import { ExecutionNeoIcon } from '@terra/ui-design-system';
import { Delete02Icon, Message01Icon } from '@terra/ui-icons';
import { ListPageBulkActionButton } from '../../common/ListPageBulkActionButton';
import { ListPageSelectionBar } from '../../common/ListPageSelectionBar';
import {
  SEND_TO_COPILOT_SIGNAL_LIMIT,
  SEND_TO_NEO_SIGNAL_LIMIT,
  SIGNALS_BULK_NEO_BLOCKER,
  resolveSignalsBulkSendAvailability,
} from '../signals-table/signals-bulk-actions.model';

type SignalsBulkActionsBarProps = {
  selectedCount: number;
  outOfTestingScopeCount: number;
  isBusy: boolean;
  mutationsDisabledTooltip: string | null;
  onClearSelection: () => void;
  onSendToNeo: () => void;
  onSendToCopilot: () => void;
  onDelete: () => void;
};

export function SignalsBulkActionsBar({
  selectedCount,
  outOfTestingScopeCount,
  isBusy,
  mutationsDisabledTooltip,
  onClearSelection,
  onSendToNeo,
  onSendToCopilot,
  onDelete,
}: SignalsBulkActionsBarProps) {
  const t = useTranslations('signals.bulkActions');
  const tSelection = useTranslations('proxy.signals.selection');
  const tCommon = useTranslations('common');
  const {
    eligibleForNeoCount,
    skippedForNeoCount,
    neoBlocker,
    isCopilotOverLimit,
  } = resolveSignalsBulkSendAvailability({
    selectedCount,
    outOfTestingScopeCount,
  });
  const isMutationDisabled = isBusy || mutationsDisabledTooltip !== null;
  const neoTooltip =
    neoBlocker === SIGNALS_BULK_NEO_BLOCKER.ALL_OUT_OF_TESTING_SCOPE
      ? t('neoOutOfTestingScopeTooltip', { count: skippedForNeoCount })
      : neoBlocker === SIGNALS_BULK_NEO_BLOCKER.OVER_LIMIT
        ? t('sendToNeoLimitTooltip', { count: SEND_TO_NEO_SIGNAL_LIMIT })
        : skippedForNeoCount > 0
          ? t('neoOutOfTestingScopePartialTooltip', {
              skipped: skippedForNeoCount,
              eligible: eligibleForNeoCount,
            })
          : t('sendToNeoTooltip');
  const copilotTooltip = isCopilotOverLimit
    ? t('sendToCopilotLimitTooltip', { count: SEND_TO_COPILOT_SIGNAL_LIMIT })
    : t('sendToCopilotTooltip');

  return (
    <ListPageSelectionBar
      selectedCount={selectedCount}
      clearSelectionLabel={tSelection('clearSelection')}
      onClearSelection={onClearSelection}
    >
      <ListPageBulkActionButton
        label={t('sendToNeo')}
        tooltip={mutationsDisabledTooltip ?? neoTooltip}
        icon={<ExecutionNeoIcon size={12} />}
        isDisabled={isMutationDisabled || neoBlocker !== null}
        onClick={onSendToNeo}
      />
      <ListPageBulkActionButton
        label={t('sendToCopilot')}
        tooltip={mutationsDisabledTooltip ?? copilotTooltip}
        icon={<Message01Icon size={12} />}
        isDisabled={isMutationDisabled || isCopilotOverLimit}
        onClick={onSendToCopilot}
      />
      <ListPageBulkActionButton
        label={tCommon('delete')}
        tooltip={mutationsDisabledTooltip ?? t('deleteTooltip')}
        icon={<Delete02Icon size={12} />}
        isDisabled={isMutationDisabled}
        onClick={onDelete}
      />
    </ListPageSelectionBar>
  );
}
